import { useId } from "react";
import { ArrowUpRight, ChartNoAxesCombined, Target, Quote } from "lucide-react";
import { mmMediaCaseStudy, type CaseStudy } from "@/data/caseStudies";

export function CaseStudySection({ study }: { study: CaseStudy }) {
  const titleId = useId();
  const maximum = Math.max(...study.monthlyTraffic.map(row => row.visits));
  const spansYears = new Set(study.monthlyTraffic.map(row => row.year)).size > 1;

  return (
    <section aria-labelledby={titleId} className="border-y border-border py-12 md:py-16">
      <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-8 md:gap-12 items-center mb-12">
        <div>
          <p className="flex items-center gap-3 text-sm font-bold text-foreground mb-5">
            <span aria-hidden="true" className="h-0.5 w-8 bg-accent" />
            Case Study
          </p>
          <h2 id={titleId} className="text-2xl md:text-4xl font-bold text-foreground leading-tight max-w-3xl mb-5">
            {study.title}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-2xl">{study.clientDescription}</p>
        </div>
        <div aria-hidden="true" className="md:border-l border-border md:pl-10">
          <div className="flex items-start gap-1 text-foreground">
            <span className="text-6xl md:text-7xl font-bold tabular-nums">+{study.headlineGrowth}<span className="text-3xl md:text-4xl">%</span></span>
            <ArrowUpRight className="w-8 h-8 text-accent" />
          </div>
          <p className="text-sm text-muted-foreground mt-3 max-w-64">{study.growthLabel}</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div className="space-y-8">
          {[
            { heading: "Starting point", text: study.startingPoint, icon: Target },
            { heading: "What Linkzy did", text: study.work, icon: ArrowUpRight },
            { heading: "Results", text: study.results, icon: ChartNoAxesCombined },
          ].map(({ heading, text, icon: Icon }) => (
            <div key={heading} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-4">
              <div className="flex items-center justify-center w-10 h-10 border border-border rounded-lg bg-card">
                <Icon aria-hidden="true" className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-foreground mb-2">{heading}</h3>
                <p className={heading === "Results" ? "text-foreground font-semibold leading-relaxed" : "text-muted-foreground leading-relaxed"}>{text}</p>
              </div>
            </div>
          ))}
        </div>
        <figure className="min-w-0">
          <div className="bg-primary text-primary-foreground rounded-lg p-5 sm:p-8 shadow-lg">
            <div className="flex items-center justify-between gap-4 mb-8">
              <h3 className="font-bold text-lg">{study.chartTitle}</h3>
              <ChartNoAxesCombined aria-hidden="true" className="w-6 h-6 shrink-0 text-primary-foreground/60" />
            </div>
            <ul aria-label={study.chartLabel} className="space-y-5">
              {study.monthlyTraffic.map((row, index) => (
                <li key={`${row.month}-${row.year}`} className="group grid grid-cols-[4.75rem_minmax(0,1fr)_3.5rem] sm:grid-cols-[5.5rem_minmax(0,1fr)_4.5rem] gap-2 sm:gap-3 items-center text-xs sm:text-sm">
                  <span className="text-primary-foreground/80">{row.month}{spansYears && <span className="block">{row.year}</span>}</span>
                  <svg aria-hidden="true" viewBox={`0 0 ${maximum} 16`} preserveAspectRatio="none" className="w-full h-6 bg-primary-foreground/10 rounded-sm overflow-hidden">
                    <rect width={row.visits} height="16" className={`${index === study.monthlyTraffic.length - 1 ? "fill-accent" : "fill-primary-foreground/70"} motion-safe:transition-opacity group-hover:opacity-80`} />
                  </svg>
                  <span className="font-bold text-right tabular-nums">{row.visits.toLocaleString("en-GB")}</span>
                </li>
              ))}
            </ul>
          </div>
          <figcaption className="text-xs text-muted-foreground leading-relaxed mt-4">{study.source}</figcaption>
        </figure>
      </div>
      {study.testimonial && <div className="grid md:grid-cols-[auto_minmax(0,1fr)_auto] gap-5 md:gap-8 border-t border-border mt-12 pt-8">
        <Quote aria-hidden="true" className="w-9 h-9 text-foreground/30" />
        <blockquote className="md:col-span-2">
          <p className="text-foreground text-lg md:text-xl leading-relaxed max-w-4xl">"{study.testimonial.quote}"</p>
          <footer className="text-sm text-muted-foreground mt-5">
            <span className="font-semibold text-foreground">{study.testimonial.author}</span>
            <span aria-hidden="true" className="mx-2">·</span>
            {study.testimonial.role}, {study.testimonial.company}
          </footer>
        </blockquote>
      </div>}
      <p className="text-xs text-muted-foreground leading-relaxed mt-7">{study.footnote}</p>
    </section>
  );
}

export default function MMMediaCaseStudy() {
  return <CaseStudySection study={mmMediaCaseStudy} />;
}